import { Subject, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { PayrollDigest } from '@models/payroll/payroll-digest.model';

@Injectable()
export class PayrollDigestService {
    private showingArchived: boolean;
    private currentPage: number;
    private totalPages = Infinity;
    private payrollDigest: PayrollDigest[];
    private subject$ = new Subject<PayrollDigest[]>();

    get payrollDigest$(): Observable<PayrollDigest[]> {
        return this.subject$.asObservable();
    }

    loadInitial(archived: boolean): Promise<void> {
        this.showingArchived = archived;
        this.currentPage = 1;
        this.totalPages = Infinity;
        this.payrollDigest = [];

        return this.fetchAndUpdate();
    }

    hasMore(): boolean {
        return this.currentPage < this.totalPages;
    }

    loadMore(): Promise<void> {
        if (!this.payrollDigest.length || !this.hasMore()) return Promise.resolve();

        this.currentPage = this.currentPage + 1;

        return this.fetchAndUpdate();
    }

    archivePayrollDigestItem(id: number): void {
        if (this.showingArchived) return;

        const index = this.payrollDigest.findIndex((item) => item.id === id);
        this.payrollDigest.splice(index, 1);

        // Reload all pages to fix pagination
        this.reloadPages();
    }

    unarchivePayrollDigestItem(id: number): void {
        if (!this.showingArchived) return;

        const index = this.payrollDigest.findIndex((item) => item.id === id);
        this.payrollDigest.splice(index, 1);

        // Reload all pages to fix pagination
        this.reloadPages();
    }

    private async reloadPages() {
        this.payrollDigest = [];

        /* eslint-disable no-plusplus */
        for (let i = 1; i <= this.currentPage; i++) {
            await this.fetch(i);
        }

        this.update();
    }

    private fetchAndUpdate() {
        return this.fetch(this.currentPage).then(() => this.update());
    }

    private async fetch(page: number) {
        // If we try to destructure the array right here, the compiler
        // turns the `await` into a `yield` statement 🤔
        const response = await PayrollDigest.page(page).where('archived', +this.showingArchived).get();
        const [data, meta] = response;

        this.payrollDigest = [...this.payrollDigest, ...data];
        this.totalPages = Math.ceil(meta.pagination.total / meta.pagination.perPage);
    }

    private update() {
        this.subject$.next(this.payrollDigest);
    }
}
